import { Link } from "react-router-dom";
import { FiArrowRight, FiCheck, FiBook, FiLayers, FiGift, FiUsers } from "react-icons/fi";
import useFetchSubscriptions from "api/SubscriptionsResource/useFetchSubscriptions";
import useCurrencyStore from "store/currencyStore";
import { priceInCurrency, formatCurrencyAmount } from "utils";

const options = [
	{
		icon: FiGift,
		title: "Start with free books",
		price: "Free titles",
		description: "Discover titles marked Free and add something new to your reading list.",
		details: ["Selected free titles", "An account is required", "Free-book limits apply"],
		to: "/books?pricing_model_is=free",
		cta: "Explore free books",
	},
	{
		icon: FiBook,
		title: "Buy a book you love",
		price: "Priced per title",
		description: "Choose a specific book and see its price and available format before you buy.",
		details: ["Individual book purchases", "Digital and print options vary by title", "Digital books are read in the app"],
		to: "/books?pricing_model_is=sellable",
		cta: "Browse books to buy",
	},
	{
		icon: FiLayers,
		title: "Choose a subscription",
		price: "Priced by plan",
		description: "Pick eligible books from the subscription collection for your plan’s duration.",
		details: ["Individual and institution plans", "Book picks and duration vary by plan", "Review current prices and limits"],
		to: "/subscriptions",
		cta: "Compare plans",
	},
];

const ReadingOptions = () => {
	const { data } = useFetchSubscriptions();
	const currency = useCurrencyStore((state) => state.currency);
	const plans = Array.isArray(data?.data) ? data.data : [];
	const startingPlan = plans
		.filter((plan) => plan.category?.toLowerCase() === "individual")
		.map((plan) => ({ ...plan, amount: priceInCurrency(plan.prices, currency, plan.cost, plan.zwl_price) }))
		.filter((plan) => Number.isFinite(plan.amount) && plan.amount > 0 && Number(plan.duration) > 0 && plan.time_unit)
		.sort((a, b) => a.amount - b.amount)[0];

	return (
		<section id="reading-options" className="home-section home-options" aria-labelledby="options-heading">
			<div className="home-wrap">
				<div className="home-centered-heading">
					<p className="home-eyebrow">YOUR READING, YOUR WAY</p>
					<h2 id="options-heading">Choose how you read.</h2>
					<p>Start with free titles, buy a book, or explore a subscription.<br />You can browse the catalogue before creating an account.</p>
				</div>
				<div className="home-options-grid">
					{options.map(({ icon: Icon, title, price, description, details, to, cta }) => (
						<article className="home-option" key={title}>
							<Icon className="home-option-icon" aria-hidden="true" />
							<h3>{title}</h3>
							<p className="home-option-price">{to === "/subscriptions" && startingPlan ? `From ${formatCurrencyAmount(startingPlan.amount, currency)}` : price}</p>
							{to === "/subscriptions" && startingPlan && <p className="home-plan-terms">For {startingPlan.duration} {startingPlan.time_unit}{Number(startingPlan.max_number_of_books) > 0 ? ` · ${startingPlan.max_number_of_books} book picks` : ""}. Individual plan.</p>}
							<p>{description}</p>
							{/* eslint-disable-next-line jsx-a11y/no-redundant-roles -- Safari drops list semantics when list-style is none. */}
							<ul role="list">{details.map((detail) => <li key={detail}><FiCheck aria-hidden="true" />{detail}</li>)}</ul>
							<Link to={to} className="home-text-link">{cta}<FiArrowRight aria-hidden="true" /></Link>
						</article>
					))}
				</div>
				<div className="home-institution-note">
					<FiUsers aria-hidden="true" />
					<p><strong>Building a library for your school?</strong> Let’s help you find the right access for your learners.</p>
					<Link to="/support" className="home-text-link">Talk to our team <FiArrowRight aria-hidden="true" /></Link>
				</div>
			</div>
		</section>
	);
};
export default ReadingOptions;
